import { Component } from '@angular/core';
import { OnInit, Input } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfigService } from '../../../services/config.service';
import { AlertsService } from '../../../services/alerts.service';
import { MetadataService } from '../../../services/metadata.service';
import { FilingService } from '../../services/filing.service';
import { UserService } from '../../../services/user.service';
import { SearchService } from '../../../search/services/search.service';
import { CopyLinkComponent } from '../../../components/copy-link/copy-link.component';
import dayjs from 'dayjs';

/**
 * Component for viewing records management data
 * for the given product record.
 */
@Component({
	selector: 'rm-record-quick-view',
	templateUrl: './record-quick-view.component.html',
	styleUrl: './record-quick-view.component.css'
})
export class RecordQuickViewComponent implements OnInit {
	@Input() productId: any;

	LOCALIZATION: any;
	Object = Object;

	recordTitle: string = '';
	record: { [key: string]: any } = {};
	searchableMetadataFields: any[] = [];
	
	constructor(
		private configService: ConfigService,
		private alertsService: AlertsService,
		private metadataService: MetadataService,
		private filingService: FilingService,
		private userService: UserService,
		private searchService: SearchService,
		private ngbModal: NgbModal,
		private ngbmodalService: NgbActiveModal
	) { }

	ngOnInit(): void {
		this.LOCALIZATION = this.userService.LOCALIZATION;
		this.searchableMetadataFields = this.configService.getConfig().search.searchableMetadataFields;
		this.loadRecord();
	}
	
	/**
	 * Loads the record data for the product.
	 */
	loadRecord(): void {
		this.filingService.getRecordForProduct(this.productId, true).then(
			(response: any) => {
			    this.processMetadata(response);
			},
			(err: any) => {
				console.error("Failed to load record data", err);
			    this.alertsService.error({
			        message: "Failed to retrieve record information for this document."
			    });
			}
		);		
	}
	
	private processMetadata(result: any) {
		let dateTypeProperty = this.userService.user.properties.awpropertyObjectList.find(function(x: any) { return x.name == "USER_INTERFACE_DATE_TYPE"; });
		let dateFormatDisplay = dateTypeProperty?.value || "MM/DD/YY";

		// See the RM schema in KV-2260 for the set of RM fields a user should have available
		let validFields = this.userService.user.properties.awfieldObjectList.filter(function(x: any) { return x.isAvailable; });
		// Add in special display-only fields not in the domain schema or user prefs
		validFields.push({
			name: 'rm_disposition_description',
			displayName: this.LOCALIZATION.rmDispositionDesc,
			isLink: false,
			type: 'STRING',
		});
		validFields.push({
			name: 'expiry_action_log',
			displayName: this.LOCALIZATION.rmDispositionLog,
			isLink: false,
			type: 'STRING',
		});


		let productInfo: { [key: string]: any } = {};

		for (let p = 0; p < validFields.length; p++) {
			let field = validFields[p];

			if (!result[field.name]) {
				continue;
			}

			productInfo[field.name] = {
				label: field.displayName,
				value: result[field.name],
				maxLength: (field.isLink ? -1 : 220),
				links: null,
				name: field.name
			};
			
			productInfo[field.name].cleanedValue = this.metadataService.cleanValue( productInfo[field.name].value );
			productInfo[field.name].isValidCollection = 
				this.metadataService.isValidFieldCollectionValue( productInfo[field.name].name, productInfo[field.name].cleanedValue );

			if (field.type == "DATE") {
				productInfo[field.name].value = dayjs( productInfo[field.name].value ).format(dateFormatDisplay);
			}

			if (field.isLink) {
				productInfo[field.name].links = this.metadataService.linksFromString( result[field.name], field.name );
			}
		}

		this.record = productInfo;
		this.recordTitle = result.title || '';
	}
	
	displayLinkMsg(link: string): void {
		if (link.startsWith("file://")) {
			const modalRef = this.ngbModal.open(CopyLinkComponent);
			modalRef.componentInstance.link = link;

			modalRef.result.then(
				(payload) => {
					console.log('Modal dismissed');
				},
				(reason) => {
					console.log('Modal dismissed');
				}
			);
		}
	}	
	
	/**
	 * Executes a search using the field and value.
	 */	
	resultFieldSearch(field: string, value: string) : void {
		console.log(`Searching for: ${field}, with value: ${value}`);
		
		let payload = {
		    field: field,
		    value: value
		};
		this.searchService.broadcastSearchResultFieldEvent(payload);
		
		this.close();
	}	
	
	/**
	 * Closes the modal.
	 */
	close(): void {
		this.ngbmodalService.dismiss();
	}
}
